import { Alert } from '@creditornot/cb-alert';
import { Button } from '@creditornot/cb-button';
import { FormRow, InputContainer } from '@creditornot/cb-form';
import { cssThemedValue } from '@creditornot/cb-ingredients';
import { Input } from '@creditornot/cb-input';
import { ModalFooter, ModalHeader } from '@creditornot/cb-modal';
import { FORM_ERROR, FormApi } from 'final-form';
import React, { useMemo, useCallback } from 'react';
import { Field, Form } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import * as yup from 'yup';

import * as api from 'apis/restaurant-api/merchant-admin/menu2/api';
import { Category } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { RFFFieldErrorWrapper, WoltModal } from 'common/components';
import validateYupSchema from 'common/utils/validateYupSchema';
import { getModifiedFieldsForTracking, useEditSubcategoryTracking } from 'modules/menu/hooks/telemetryHooks';
import useMenuData from 'modules/menu/useMenuData';

import { reportCategoryEdit } from '../../behavioral';
import { UpdateCategoryFormData } from '../types';
import { buildUpdateCategoryFormData, buildCategoryUpdateData } from '../utils';

type EditSubcategoryModalProps = {
  category: Category;
  className?: string;
  isOpen: boolean;
  onClose: () => void;
};

const MAX_NAME_LENGTH = 50;

const Root = styled(WoltModal)`
  width: 700px;
  background: ${cssThemedValue('surfaceMain')};
  border-radius: 8px;
`;

const StyledFooter = styled(ModalFooter)`
  display: flex;
  flex-direction: column;
`;

const Buttons = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: flex-end;
`;

const ConfirmButton = styled(Button).attrs({ variant: 'blue', size: 'small' })``;

const CancelButton = styled(Button).attrs({ variant: 'lightBlue', size: 'small' })`
  margin-inline-end: 16px;
`;

const SubmitError = styled(Alert).attrs({ variant: 'error' })`
  margin: 0 0 16px;
`;

const validationSchema = yup
  .object({
    name: yup.string().trim().required(),
  })
  .required();

export const EditSubcategoryModal = ({ category, className, isOpen, onClose }: EditSubcategoryModalProps) => {
  const { menu, updateMenuData } = useMenuData();

  if (!menu) {
    throw new Error('Menu not found, this should not happen!');
  }

  const intl = useIntl();

  const initialValues = useMemo(() => buildUpdateCategoryFormData(menu.primary_language, category), [menu, category]);
  const trackEditSubcategory = useEditSubcategoryTracking();
  const handleFormSubmit = useCallback(
    async (
      formData: UpdateCategoryFormData,
      form: FormApi<UpdateCategoryFormData, Partial<UpdateCategoryFormData>>,
    ) => {
      try {
        const data = buildCategoryUpdateData(menu.primary_language, category, formData, null);
        const updatedMenu = await api.updateCategory(menu.id, menu.version, data);
        updateMenuData({ actionType: 'updateMenu', payload: updatedMenu });
        const modifiedFields = getModifiedFieldsForTracking<UpdateCategoryFormData>(form.getState());
        trackEditSubcategory(category.id, modifiedFields);
        reportCategoryEdit();
        onClose();
        return;
      } catch (error) {
        return { [FORM_ERROR]: intl.formatMessage({ id: 'errors.subcategory.addSubcategoryError' }) };
      }
    },
    [category, intl, menu, onClose, updateMenuData, trackEditSubcategory],
  );

  return (
    <Root className={className} isOpen={isOpen} onRequestClose={onClose}>
      <ModalHeader onCloseClick={onClose}>
        <FormattedMessage id="menu.subcategory.edit-header" />
      </ModalHeader>
      <Form<UpdateCategoryFormData>
        onSubmit={handleFormSubmit}
        initialValues={initialValues}
        validate={validateYupSchema(validationSchema)}
      >
        {({ handleSubmit, hasValidationErrors, pristine, submitError, submitting }) => (
          <form onSubmit={handleSubmit}>
            <FormRow label={intl.formatMessage({ id: 'menu.subcategory.name-input-label' })} horizontalPadding required>
              <Field name="name" parse={v => v}>
                {field => (
                  <RFFFieldErrorWrapper field={field}>
                    {input => (
                      <InputContainer characterCount={{ value: input.value.length, max: MAX_NAME_LENGTH }}>
                        <Input {...input} maxLength={50} invalid={!!field.meta.error && !!field.meta.touched} />
                      </InputContainer>
                    )}
                  </RFFFieldErrorWrapper>
                )}
              </Field>
            </FormRow>
            <StyledFooter>
              {submitError && <SubmitError>{submitError}</SubmitError>}
              <Buttons>
                <CancelButton onClick={onClose} disabled={submitting}>
                  <FormattedMessage id="state.cancel" />
                </CancelButton>
                <ConfirmButton
                  type="submit"
                  onClick={handleSubmit}
                  disabled={pristine || submitting || hasValidationErrors}
                  loading={submitting}
                >
                  <FormattedMessage id="state.save" />
                </ConfirmButton>
              </Buttons>
            </StyledFooter>
          </form>
        )}
      </Form>
    </Root>
  );
};
